import { css } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { Button, Text, useForm, type FormFieldState, type ValidatorFieldState } from '@frontend/design-system';
import { PinField, PIN_LENGTH } from './pin-field';
import { getPinValidationError } from './pin-validation';

export type CreatePinFormProps = {
  onSubmit: (pin: string) => void;
  submitLabel: string;
  isSubmitting?: boolean;
  length?: number;
};

const onlyDigits =
  (length: number) =>
  (value = '') =>
    value.replace(/\D/g, '').slice(0, length);

export function CreatePinForm({
  onSubmit,
  submitLabel,
  isSubmitting = false,
  length = PIN_LENGTH,
}: CreatePinFormProps) {
  const { t } = useTranslation('account');

  const { getFieldProps, formProps, isComplete } = useForm({
    fields: {
      pin: {
        type: 'text',
        required: true,
        validateOnChange: true,
        transformValue: onlyDigits(length),
        validator: (field: ValidatorFieldState<'text'>) => {
          switch (getPinValidationError(field.value, length)) {
            case 'length':
              return t('Enter a {{length}}-digit PIN', { length });
            case 'repeated':
              return t('Choose a PIN that is not the same digit repeated.');
            case 'sequential':
              return t('Choose a PIN that is not a sequence of digits.');
            default:
              return '';
          }
        },
      },
      confirmPin: {
        type: 'text',
        required: true,
        validateOnChange: true,
        transformValue: onlyDigits(length),
        validator: (field: ValidatorFieldState<'text'>, allFields: { pin: FormFieldState<'text'> }) => {
          if (field.value !== allFields?.pin?.value) {
            return t('PINs do not match');
          }
          return '';
        },
      },
    },
    onSubmit: (values) => {
      if (!values.pin) {
        return;
      }
      onSubmit(values.pin);
    },
  });

  return (
    <form {...formProps} id='create-pin-form' data-testid='create-pin-form'>
      <PinField
        {...getFieldProps('pin')}
        label={t('{{length}}-digit PIN', { length })}
        length={length}
        disabled={isSubmitting}
        autoFocus
      />
      <Text
        size='small'
        color='light'
        css={css`
          margin: ${theme.spacing(1)} 0 ${theme.spacing(3)};
        `}
      >
        {t('Choose {{length}} digits that are easy for you to remember but hard for others to guess.', { length })}
      </Text>
      <PinField
        {...getFieldProps('confirmPin')}
        label={t('Confirm PIN')}
        length={length}
        disabled={isSubmitting}
        css={css`
          margin-bottom: ${theme.spacing(3)};
        `}
      />
      <Button
        type='submit'
        variant='primary'
        size='large'
        disabled={!isComplete || isSubmitting}
        loading={isSubmitting}
        id='set-pin-button'
        css={css`
          width: 100%;
        `}
      >
        {submitLabel}
      </Button>
    </form>
  );
}
